import type { ReactNode } from 'react';
import { Entity } from '@backstage/catalog-model';
import { Avatar, Card, CardBody, Flex, Text } from '@backstage/ui';

interface PersonMiniCardProps {
  entity: Entity;
  /** First line below the name (e.g. date info) */
  line1: string;
  /** Optional second line (e.g. "X days ago") */
  line2?: string;
  /** Optional badge rendered in the top-right corner of the card */
  badge?: ReactNode;
}

type UserProfile = { displayName?: string; picture?: string };

export const PersonMiniCard = ({
  entity,
  line1,
  line2,
  badge,
}: PersonMiniCardProps) => {
  const profile = (entity.spec as { profile?: UserProfile } | undefined)
    ?.profile;
  const displayName =
    profile?.displayName ?? entity.metadata.title ?? entity.metadata.name;
  const picture = profile?.picture;
  const href = `/catalog/${entity.metadata.namespace ?? 'default'}/user/${entity.metadata.name}`;
  const subtitle =
    entity.metadata.annotations?.['okta.com/job-title'] ??
    entity.metadata.annotations?.['okta.com/job-division'];

  return (
    <div style={{ position: 'relative' }}>
      {badge && (
        <span
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {badge}
        </span>
      )}
      <Card href={href} label={displayName}>
        <CardBody>
          <Flex direction="column" align="center" gap="1">
            <Avatar src={picture ?? ''} name={displayName} size="x-large" />
            <Text
              variant="body-small"
              weight="bold"
              // break-word splits mid-name only as a last resort
              style={{ textAlign: 'center', overflowWrap: 'break-word' }}
            >
              {displayName}
            </Text>
            {subtitle && (
              <Text
                variant="body-x-small"
                color="secondary"
                style={{ textAlign: 'center' }}
              >
                {subtitle}
              </Text>
            )}
            <Text
              variant="body-x-small"
              color="secondary"
              style={{ textAlign: 'center' }}
            >
              {line1}
            </Text>
            {line2 && (
              <Text
                variant="body-x-small"
                weight="bold"
                style={{ textAlign: 'center' }}
              >
                {line2}
              </Text>
            )}
          </Flex>
        </CardBody>
      </Card>
    </div>
  );
};
